import { ExternalLinkIcon } from "lucide-react"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"

import { AppBadge } from "@/components/commons"
import { Button } from "@/components/ui/button"
import { TableCell, TableRow } from "@/components/ui/table"
import { FRONTEND_ROUTES } from "@/constants"
import type { InstallmentsStatusType } from "@/helpers"
import { getStatusInfo } from "@/helpers"
import { formatMoney } from "@/helpers/format"
import { DateHelper } from "@/utils"

interface ScheduleTableRowProps {
  installment: ReceivablesInstallmentType
}

export const ScheduleTableRow = ({
  installment,
}: ScheduleTableRowProps) => {
  const { t } = useTranslation()

  return (
    <TableRow className="group">
      <TableCell className="font-medium">
        <div className="flex flex-col gap-1">
          <span className="font-mono text-sm">
            {installment.orderCode}
          </span>
          <div className="flex flex-col gap-0.5 lg:hidden">
            <span className="text-xs text-muted-foreground">
              {DateHelper.formatDate(
                installment.date,
                "dd/MM/yyyy"
              )}
            </span>
            <span className="text-xs">
              Parcela {installment.installmentNumber}/
              {installment.totalInstallment}
            </span>
            <div className="flex items-center gap-2">
              <AppBadge
                getBadgeInfo={() =>
                  getStatusInfo(
                    installment.status as InstallmentsStatusType
                  )
                }
              />
              <span className="font-semibold">
                {formatMoney(installment.netValue / 100)}
              </span>
            </div>
          </div>
        </div>
      </TableCell>

      <TableCell className="hidden lg:table-cell">
        <span className="text-sm">
          {DateHelper.formatDate(
            installment.date,
            "dd/MM/yyyy"
          )}
        </span>
      </TableCell>

      <TableCell className="hidden text-center lg:table-cell">
        <span className="text-sm">
          {installment.installmentNumber}/
          {installment.totalInstallment}
        </span>
      </TableCell>

      <TableCell className="hidden text-end lg:table-cell">
        <span className="text-sm font-medium">
          {formatMoney(installment.value / 100)}
        </span>
      </TableCell>

      <TableCell className="hidden text-end lg:table-cell">
        <span className="text-sm text-red-600">
          {formatMoney(installment.commissionValue / 100)}
        </span>
      </TableCell>

      <TableCell className="hidden text-end lg:table-cell">
        <span className="text-sm text-red-600">
          {formatMoney(installment.cashbackValue / 100)}
        </span>
      </TableCell>

      <TableCell className="hidden text-end font-semibold lg:table-cell">
        <span className="text-sm">
          {formatMoney(installment.netValue / 100)}
        </span>
      </TableCell>

      <TableCell className="hidden text-center lg:table-cell">
        <AppBadge
          getBadgeInfo={() =>
            getStatusInfo(
              installment.status as InstallmentsStatusType
            )
          }
        />
      </TableCell>

      <TableCell className="text-center">
        <Button
          variant="ghost"
          asChild
          size="icon"
          className="h-8 w-8"
        >
          <Link
            to={`${FRONTEND_ROUTES.SCHEDULE.href}/details/${installment.transactionId}`}
          >
            <span className="sr-only">
              {t("common.viewDetails")}
            </span>
            <ExternalLinkIcon
              size={16}
              strokeWidth={2}
              aria-hidden="true"
            />
          </Link>
        </Button>
      </TableCell>
    </TableRow>
  )
}
